import { AlertCircle, ChevronLeft, ChevronRight, Loader2, Save, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { StepAPersonalInfo } from '../components/forms/StepAPersonalInfo';
import { StepBEligibility } from '../components/forms/StepBEligibility';
import { StepCLicensing, type LicenseDocType } from '../components/forms/StepCLicensing';
import { StepDAvailability } from '../components/forms/StepDAvailability';
import { StepEPreferences } from '../components/forms/StepEPreferences';
import { StepFConsents } from '../components/forms/StepFConsents';
import { useAutoSave } from '../hooks/useAutoSave';
import { formatUsPhone } from '../lib/phone';
import { toast } from '../lib/toast';
import { mapValidationMessages } from '../lib/validationMessages';
import { validateStep } from '../lib/validations';
import { useApplicationStore } from '../store/applicationStore';
import { useAuthStore } from '../store/authStore';

interface WizardStep {
  key: 'stepA' | 'stepB' | 'stepC' | 'stepD' | 'stepE' | 'stepF';
  label: string;
}

export function WizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const setJobPosting = useApplicationStore((state) => state.setJobPosting);
  const jobPostingTitle = useApplicationStore((state) => state.jobPostingTitle);
  const jobPostingLocation = useApplicationStore((state) => state.jobPostingLocation);
  const jobPostingSiteState = useApplicationStore((state) => state.jobPostingSiteState);
  const data = useApplicationStore((state) => state.data);
  const currentStep = useApplicationStore((state) => state.currentStep);
  const nextStep = useApplicationStore((state) => state.nextStep);
  const prevStep = useApplicationStore((state) => state.prevStep);
  const goToStep = useApplicationStore((state) => state.goToStep);
  const updateStepData = useApplicationStore((state) => state.updateStepData);
  const saveAndValidateStep = useApplicationStore((state) => state.saveAndValidateStep);
  const isLoading = useApplicationStore((state) => state.isLoading);
  const loadDraft = useApplicationStore((state) => state.loadDraft);
  const loadPrefill = useApplicationStore((state) => state.loadPrefill);
  const checkExistingApplications = useApplicationStore((state) => state.checkExistingApplications);
  const isUserAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const saveDraftQuietly = useApplicationStore((state) => state.saveDraftQuietly);

  // Autosave 2 seconds after data changes
  useAutoSave(saveDraftQuietly, [data], 2000);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [warnings, setWarnings] = useState<Record<string, string>>({});
  const [isInitializing, setIsInitializing] = useState(true);

  // Clear stale errors as soon as the user starts editing, so the banner
  // dismisses once they begin fixing the problem (instead of persisting until
  // the next Next click). Errors only get re-populated by handleNext/handleStepClick.
  useEffect(() => {
    setErrors((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [data]);
  const [showPrefillBanner, setShowPrefillBanner] = useState(false);
  const [licensingUploads, setLicensingUploads] = useState<Partial<Record<LicenseDocType, string>>>({});
  const prefillAttempted = useRef(false);
  const draftLoadAttempted = useRef(false);
  const hasIncomingWizardState = useRef(false);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);

  const steps: WizardStep[] = useMemo(
    () => [
      { key: 'stepA', label: t('wizard.steps.personal') },
      { key: 'stepB', label: t('wizard.steps.eligibility') },
      { key: 'stepC', label: t('wizard.steps.licensing') },
      { key: 'stepD', label: t('wizard.steps.availability') },
      { key: 'stepE', label: t('wizard.steps.preferences') },
      { key: 'stepF', label: t('wizard.steps.consents') },
    ],
    [t],
  );

  useEffect(() => {
    const state = location.state as
      | {
          step?: number;
          errors?: Record<string, string>;
          warnings?: Record<string, string>;
          jobPostingId?: string;
          jobPostingTitle?: string;
          jobPostingLocation?: string;
          jobPostingSiteState?: string;
        }
      | null
      | undefined;
    if (!state) return;

    hasIncomingWizardState.current = true;

    if (state.jobPostingId) {
      setJobPosting(state.jobPostingId, state.jobPostingTitle, state.jobPostingLocation, state.jobPostingSiteState);
    }
    if (typeof state.step === 'number') {
      goToStep(state.step);
    }
    if (state.errors) {
      setErrors(state.errors);
    }
    if (state.warnings) {
      setWarnings(state.warnings);
    }

    void navigate(location.pathname, { replace: true, state: null });
  }, [goToStep, location.pathname, location.state, navigate, setJobPosting]);

  // Redirect if application already submitted
  useEffect(() => {
    if (data.status === 'submitted') {
      void navigate('/success', { replace: true });
    }
  }, [data.status, navigate]);

  // Load draft from API on mount (skip for guests — their data is in localStorage only)
  useEffect(() => {
    if (draftLoadAttempted.current) return;
    if (data.status === 'submitted') {
      setIsInitializing(false);
      return;
    }

    // Returning from preview with explicit wizard state (step/errors/warnings):
    // keep current in-memory state and avoid reloading draft that can overwrite step.
    if (hasIncomingWizardState.current) {
      setIsInitializing(false);
      return;
    }

    draftLoadAttempted.current = true;

    // Guest users: skip API draft loading, use localStorage data via Zustand persist
    if (!isUserAuthenticated) {
      setIsInitializing(false);
      return;
    }

    loadDraft()
      .then(async (hasDraft) => {
        // If no draft found, load prefill from prior applications without blocking re-application.
        if (!hasDraft) {
          const [, hasPrefillData] = await Promise.all([checkExistingApplications(), loadPrefill()]);
          if (hasPrefillData) {
            setShowPrefillBanner(true);
          }
        }
        setIsInitializing(false);
      })
      .catch(() => {
        toast.error(t('wizard.errors.loadFailed'));
        setIsInitializing(false);
      });
  }, [data.status, isUserAuthenticated, loadDraft, loadPrefill, checkExistingApplications, navigate, t]);

  // Prefill from OTP contact (only once on mount when personal info is blank).
  useEffect(() => {
    if (prefillAttempted.current) return;
    prefillAttempted.current = true;

    const contact = localStorage.getItem('applicant_contact');
    if (!contact) return;

    const stepA = data.stepA;
    const hasAny = Boolean(stepA.email) || Boolean(stepA.phone) || Boolean(stepA.firstName) || Boolean(stepA.lastName);
    if (hasAny) return;

    if (contact.includes('@')) {
      updateStepData('stepA', { email: contact });
    } else {
      updateStepData('stepA', { phone: formatUsPhone(contact) });
    }
  }, [data.stepA, updateStepData]);

  const progressPercent = ((currentStep + 1) / steps.length) * 100;
  const isLastStep = currentStep === steps.length - 1;

  const validateCurrentStep = useCallback((): { ok: boolean; errorCount: number } => {
    const step = steps[currentStep];
    if (!step) return { ok: true, errorCount: 0 };

    const result = validateStep(step.key, data);
    if (result.success) {
      setErrors({});
      return { ok: true, errorCount: 0 };
    }

    setErrors(result.errors);
    return { ok: false, errorCount: Object.keys(result.errors).length || 1 };
  }, [currentStep, data, steps]);

  const handleNext = useCallback(async () => {
    const step = steps[currentStep];
    if (!step) return;

    // Client-side validation first
    const localValidation = validateCurrentStep();
    if (!localValidation.ok) {
      toast.error(t('wizard.validation.fixErrors', { count: localValidation.errorCount }));
      // Scroll to first error field for better UX
      setTimeout(() => {
        const firstErrorField = document.querySelector('[aria-invalid="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (firstErrorField as HTMLElement).focus();
        }
      }, 100);
      return;
    }

    try {
      if (isLastStep) {
        // Guest path: redirect to auth before allowing preview/submit
        if (!isUserAuthenticated) {
          toast.info(t('wizard.guest.loginRequired'));
          void navigate('/auth', { state: { returnTo: '/wizard', returnToSubmit: true } });
          return;
        }

        const result = await saveAndValidateStep();

        if ('conflict' in result && result.conflict) {
          toast.error(t('wizard.errors.alreadyApplied', 'You already have an application for this job posting.'));
          void navigate('/dashboard');
          return;
        }

        if (!result.success && result.errors) {
          setErrors(mapValidationMessages(result.errors));
          setWarnings(result.warnings ? mapValidationMessages(result.warnings) : {});
          toast.error(t('wizard.validation.fixErrors', { count: result.errors.length }));
          return;
        }

        if (result.warnings) {
          setWarnings(mapValidationMessages(result.warnings));
          for (const warning of result.warnings) {
            toast.warning(warning.message);
          }
        } else {
          setWarnings({});
        }

        setErrors({});
        void navigate('/preview');
        return;
      }

      // Save and validate step via API
      const result = await saveAndValidateStep();

      // Handle duplicate application conflict
      if ('conflict' in result && result.conflict) {
        toast.error(t('wizard.errors.alreadyApplied', 'You already have an application for this job posting.'));
        void navigate('/dashboard');
        return;
      }

      // Handle server-side validation errors
      if (!result.success && result.errors) {
        setErrors(mapValidationMessages(result.errors));
        setWarnings(result.warnings ? mapValidationMessages(result.warnings) : {});
        toast.error(t('wizard.validation.fixErrors', { count: result.errors.length }));
        return;
      }

      // Show warnings (non-blocking)
      if (result.warnings) {
        setWarnings(mapValidationMessages(result.warnings));
        for (const warning of result.warnings) {
          toast.warning(warning.message);
        }
      } else {
        setWarnings({});
      }

      // Advance to next step
      setErrors({});
      nextStep();
      window.scrollTo(0, 0);
      setTimeout(() => stepHeadingRef.current?.focus(), 50);
      toast.success(t('wizard.navigation.saved'));
    } catch {
      toast.error(t('wizard.errors.saveFailed'));
    }
  }, [
    currentStep,
    isLastStep,
    isUserAuthenticated,
    navigate,
    nextStep,
    saveAndValidateStep,
    steps,
    t,
    validateCurrentStep,
  ]);

  const handleBack = useCallback(() => {
    if (currentStep === 0) return;
    setErrors({});
    prevStep();
    window.scrollTo(0, 0);
    setTimeout(() => stepHeadingRef.current?.focus(), 50);
  }, [currentStep, prevStep]);

  const handleSaveExit = useCallback(async () => {
    try {
      const result = await saveAndValidateStep();

      if (!result.success && result.errors) {
        setErrors(mapValidationMessages(result.errors));
        toast.error(t('wizard.validation.fixErrors', { count: result.errors.length }));
        return;
      }

      setErrors({});
      toast.success(t('wizard.navigation.saved'));
      void navigate('/');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('wizard.errors.saveFailed');
      toast.error(message);
    }
  }, [navigate, saveAndValidateStep, t]);

  const resolveDocumentApplicationId = useCallback(async (): Promise<string | null> => {
    if (!useAuthStore.getState().isAuthenticated) {
      toast.info(t('wizard.guest.loginRequired'));
      void navigate('/auth', { state: { returnTo: '/wizard' } });
      return null;
    }

    const currentApplicationId = useApplicationStore.getState().serverApplicationId;
    if (currentApplicationId) {
      return currentApplicationId;
    }

    try {
      const result = await saveAndValidateStep();
      if ('conflict' in result && result.conflict) {
        toast.error(t('wizard.errors.alreadyApplied', 'You already have an application for this job posting.'));
        void navigate('/dashboard');
        return null;
      }

      if (!result.success && result.errors) {
        setErrors(mapValidationMessages(result.errors));
        setWarnings(result.warnings ? mapValidationMessages(result.warnings) : {});
        toast.error(t('wizard.validation.fixErrors', { count: result.errors.length }));
        return null;
      }

      return useApplicationStore.getState().serverApplicationId;
    } catch {
      toast.error(t('wizard.errors.saveFailed'));
      return null;
    }
  }, [navigate, saveAndValidateStep, t]);

  const renderCurrentStep = () => {
    const step = steps[currentStep];
    if (!step) return null;

    switch (step.key) {
      case 'stepA':
        return (
          <StepAPersonalInfo value={data.stepA} errors={errors} onChange={(patch) => updateStepData('stepA', patch)} />
        );
      case 'stepB':
        return (
          <StepBEligibility value={data.stepB} errors={errors} onChange={(patch) => updateStepData('stepB', patch)} />
        );
      case 'stepC':
        return (
          <StepCLicensing
            value={data.stepC}
            state={jobPostingSiteState ?? data.stepA.state}
            errors={errors}
            warnings={warnings}
            onChange={(patch) => updateStepData('stepC', patch)}
            licensingUploads={licensingUploads}
            onLicensingUpload={(type, thumbUrl) => setLicensingUploads((prev) => ({ ...prev, [type]: thumbUrl }))}
            resolveDocumentApplicationId={resolveDocumentApplicationId}
          />
        );
      case 'stepD':
        return (
          <StepDAvailability
            value={data.stepD}
            errors={errors}
            onChange={(patch) => updateStepData('stepD', patch)}
            applicantState={jobPostingSiteState ?? data.stepA.state}
          />
        );
      case 'stepE':
        return (
          <StepEPreferences value={data.stepE} errors={errors} onChange={(patch) => updateStepData('stepE', patch)} />
        );
      case 'stepF':
        return (
          <StepFConsents
            value={data.stepF}
            fullName={`${data.stepA.firstName} ${data.stepA.lastName}`.trim()}
            errors={errors}
            onChange={(patch) => updateStepData('stepF', patch)}
          />
        );
      default:
        return null;
    }
  };

  // Show loading state while fetching draft or checking existing applications
  if (isLoading || isInitializing) {
    return (
      <div className='flex items-center justify-center min-h-[400px]' role='status' aria-label={t('common.loading')}>
        <Loader2 className='h-8 w-8 animate-spin text-primary' aria-hidden='true' />
        <span className='sr-only'>{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className='max-w-2xl mx-auto p-4'>
      {jobPostingTitle && (
        <div className='mb-4 rounded-lg border border-blue-100 bg-brand-pale px-4 py-3'>
          <p className='text-sm font-medium text-primary'>{t('wizard.applyingFor', { title: jobPostingTitle })}</p>
          {jobPostingLocation && <p className='text-xs text-gray-500 mt-0.5'>{jobPostingLocation}</p>}
        </div>
      )}

      {showPrefillBanner && (
        <div
          role='status'
          className='mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start justify-between gap-3'>
          <p className='text-sm text-amber-800'>{t('wizard.prefill.banner')}</p>
          <button
            type='button'
            onClick={() => setShowPrefillBanner(false)}
            aria-label={t('wizard.prefill.dismiss')}
            className='shrink-0 text-amber-600 hover:text-amber-800 transition-colors'>
            <X size={16} aria-hidden='true' />
          </button>
        </div>
      )}

      <nav aria-label={t('wizard.a11y.stepNavigation')} className='mb-5'>
        <div className='flex justify-between text-sm font-medium text-gray-500 mb-2'>
          <span>{t('wizard.progress.stepOf', { current: currentStep + 1, total: steps.length })}</span>
          <span aria-current='step'>{steps[currentStep]?.label}</span>
        </div>
        <div
          role='progressbar'
          aria-valuenow={currentStep + 1}
          aria-valuemin={1}
          aria-valuemax={steps.length}
          aria-label={t('wizard.progress.ariaLabel', {
            current: currentStep + 1,
            total: steps.length,
            step: steps[currentStep]?.label ?? '',
          })}
          className='w-full bg-gray-200 rounded-full h-3'>
          <div
            className='bg-primary h-3 rounded-full transition-all duration-300 ease-in-out'
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </nav>

      <div
        className='bg-white shadow-lg rounded-lg p-5 mb-20 relative'
        role='form'
        aria-label={steps[currentStep]?.label}>
        <h2 ref={stepHeadingRef} tabIndex={-1} className='sr-only'>
          {steps[currentStep]?.label ?? ''}
        </h2>
        {Object.keys(errors).length > 0 ? (
          <div
            role='alert'
            className='mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded flex items-center'>
            <AlertCircle size={18} className='mr-2' aria-hidden='true' />
            <span className='text-sm font-medium'>{t('wizard.validation.banner')}</span>
          </div>
        ) : null}

        {renderCurrentStep()}
      </div>

      <div className='fixed bottom-0 left-0 w-full bg-white border-t p-4 shadow-lg z-10'>
        <div className='max-w-2xl mx-auto flex justify-between items-center'>
          <button
            type='button'
            onClick={handleBack}
            disabled={currentStep === 0}
            aria-label={
              currentStep > 0
                ? t('wizard.a11y.backTo', { step: steps[currentStep - 1]?.label ?? '' })
                : t('common.back')
            }
            className={`flex items-center px-5 py-2.5 rounded-lg text-gray-700 font-medium ${currentStep === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}>
            <ChevronLeft size={20} className='mr-1' /> {t('common.back')}
          </button>

          <button
            type='button'
            onClick={() => void handleSaveExit()}
            className='hidden sm:flex items-center text-gray-500 hover:text-primary text-sm font-medium'>
            <Save size={16} className='mr-1' />
            {t('wizard.navigation.saveExit')}
          </button>

          <button
            type='button'
            onClick={() => void handleNext()}
            aria-label={
              isLastStep
                ? t('wizard.preview.reviewButton')
                : t('wizard.a11y.nextTo', { step: steps[currentStep + 1]?.label ?? '' })
            }
            className='flex items-center bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-lg text-base font-bold shadow-md transition'>
            {isLastStep ? t('wizard.preview.reviewButton') : t('common.next')}
            <ChevronRight size={20} className='ml-1' />
          </button>
        </div>
      </div>

      {/* Screen reader announcement for validation errors */}
      <div aria-live='assertive' className='sr-only'>
        {Object.keys(errors).length > 0 ? t('wizard.a11y.errorCount', { count: Object.keys(errors).length }) : ''}
      </div>
    </div>
  );
}
